import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Sentry } from '@/lib/sentry';
import { usePreferencesStore } from '@/state/preferences';
import type { User, AuthState, AuthActions } from '../types/auth';

function setSentryUser(user: User | null) {
  if (!Sentry) return;
  if (user) {
    const host = window.location.hostname;
    const parts = host.split('.');
    const baseDomain = parts.length >= 2 ? parts.slice(-2).join('.') : host;
    const subdomain = host.endsWith('.' + baseDomain) && host !== baseDomain
      ? host.slice(0, -(baseDomain.length + 1))
      : null;

    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.full_name,
    });
    Sentry.setTag('portal', subdomain ? 'tenant' : 'platform');
    Sentry.setTag('subdomain', subdomain || 'platform');
  } else {
    Sentry.setUser(null);
    Sentry.setTag('portal', undefined);
    Sentry.setTag('subdomain', undefined);
  }
}

export const authStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      // State
      user: null,
      isAuthenticated: false,
      isLoading: true,

      // Actions
      login: (user: User) => {
        setSentryUser(user);
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        setSentryUser(null);
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
        // Clear persisted state
        localStorage.removeItem('auth-storage');
        // Clear preferences so the next user doesn't briefly see the previous
        // user's preferences before /me hydrates. setAll({}) also broadcasts to
        // other open tabs so they drop their preferences too.
        localStorage.removeItem('preferences-storage');
        usePreferencesStore.getState().setAll({});
      },

      setUser: (user: User) => {
        setSentryUser(user);
        set({ user });
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isLoading = false;
          if (state.user) setSentryUser(state.user);
        }
      },
    }
  )
);

// Selectors
export const useAuth = () => authStore();
export const useUser = () => authStore((state) => state.user);
export const useIsAuthenticated = () => authStore((state) => state.isAuthenticated);